// Crosstalk Mobile — PDP addon controller
// Listens for checkbox changes, updates the live total, and on Add-to-Cart
// adds all selected addons to the cart alongside the main product.
(function () {
  'use strict';

  const root = document.querySelector('[data-cm-addons]');
  if (!root) return;

  const total = root.querySelector('[data-cm-total]');
  const basePrice = parseInt(root.querySelector('[data-base-price]').dataset.basePrice, 10) || 0;
  const moneyFormat = root.querySelector('[data-base-price]').dataset.moneyFormat || '${{amount}}';

  const addonsRow = total.querySelector('[data-addons-row]');
  const addonsCountEl = total.querySelector('[data-addons-count]');
  const addonsPriceEl = total.querySelector('[data-addons-price-display]');
  const grandTotalEl = total.querySelector('[data-grand-total-display]');

  function formatMoney(cents) {
    // Lightweight money formatter that respects Shopify's money_format
    // for {{amount}}, {{amount_no_decimals}}, and {{amount_with_comma_separator}}.
    const value = (cents / 100);
    const formatters = {
      '{{amount}}': value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      '{{amount_no_decimals}}': Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','),
      '{{amount_with_comma_separator}}': value.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
      '{{amount_no_decimals_with_comma_separator}}': Math.round(value).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.'),
    };
    let out = moneyFormat;
    Object.keys(formatters).forEach((token) => {
      out = out.replaceAll(token, formatters[token]);
    });
    return out;
  }

  // Hardware-pairing discount when a 5G plan is added
  const BUNDLE_DISCOUNT_RATE = 0.10; // 10% off the router

  // The discount-function (cart-validation app) excludes PLAN-* items
  // from the bundle discount — only hardware gets the 10% off. Mirror
  // that here so the UI doesn't claim savings that checkout won't apply.
  // A plan PDP basePrice is the plan price; nothing should discount.
  const currentHandle = (root.dataset.productHandle || '').toLowerCase();
  const isPlanPDP = currentHandle.indexOf('crosstalk-mobile-unlimited-plan') === 0;

  const savingsRow = total.querySelector('[data-savings-row]');
  const savingsEl = total.querySelector('[data-savings-display]');
  const savingsNote = total.parentElement.querySelector('[data-savings-note]');

  function getSelectedAddons() {
    return Array.from(root.querySelectorAll('[data-cm-addon]:checked'));
  }

  function hasSelectedPlan() {
    return root.querySelector('[data-cm-addon][data-addon-type="plan"]:checked') !== null;
  }

  // Plan ↔ nested sub-option coupling. Each billing-aware nested option
  // (Static IP under each plan) carries data-paired-parent="<planId>".
  // We enable it only when its parent plan is checked; uncheck nested
  // items when their parent un-checks. This is the visual design that
  // replaces the reactive variant-swap approach.
  function syncNestedAddons() {
    const planCheckboxes = root.querySelectorAll(
      '[data-cm-addon][data-addon-type="plan"]'
    );
    const checkedPlanIds = new Set();
    planCheckboxes.forEach((el) => {
      if (el.checked) checkedPlanIds.add(el.dataset.planId);
    });

    const nestedItems = root.querySelectorAll(
      '[data-cm-addon][data-addon-type="paired-accessory"]'
    );
    nestedItems.forEach((el) => {
      const parentId = el.dataset.pairedParent;
      const parentChecked = checkedPlanIds.has(parentId);
      el.disabled = !parentChecked;
      const wrap = el.closest('.cm-addons-card__item--nested');
      if (wrap) wrap.classList.toggle('is-enabled', parentChecked);
      // If the parent plan was just un-checked, drop the nested check
      // so we don't keep it in the cart silently.
      if (!parentChecked && el.checked) el.checked = false;
    });
  }

  function updateTotals() {
    const selected = getSelectedAddons();
    const addonsCents = selected.reduce(
      (sum, el) => sum + (parseInt(el.dataset.variantPrice, 10) || 0),
      0
    );

    const planSelected = hasSelectedPlan();
    // No savings when the current product is itself a plan — matches
    // the discount-function rule that excludes PLAN-* from the bundle.
    const savingsCents = (planSelected && !isPlanPDP)
      ? Math.round(basePrice * BUNDLE_DISCOUNT_RATE)
      : 0;
    const grandCents = basePrice + addonsCents - savingsCents;

    addonsCountEl.textContent = selected.length;
    addonsPriceEl.textContent = '+' + formatMoney(addonsCents);
    grandTotalEl.textContent = formatMoney(grandCents);
    addonsRow.toggleAttribute('hidden', selected.length === 0);

    if (savingsRow) {
      savingsEl.textContent = '−' + formatMoney(savingsCents);
      savingsRow.toggleAttribute('hidden', savingsCents === 0);
    }
    if (savingsNote) {
      savingsNote.toggleAttribute('hidden', savingsCents === 0);
    }
  }

  // Listen for checkbox changes
  root.addEventListener('change', (e) => {
    if (!e.target.matches('[data-cm-addon]')) return;
    // Plan changes must sync nested sub-options before totals update,
    // since unchecking a plan auto-unchecks its nested children.
    if (e.target.dataset.addonType === 'plan') syncNestedAddons();
    updateTotals();
  });

  // Run once on init to handle any pre-checked state.
  syncNestedAddons();

  // ── More-info modal with image gallery ─────────
  const modal = root.querySelector('[data-cm-modal]');
  if (modal) {
    const m = {
      gallery: modal.querySelector('[data-cm-modal-gallery]'),
      slides: modal.querySelector('[data-cm-modal-slides]'),
      prev: modal.querySelector('[data-cm-modal-prev]'),
      next: modal.querySelector('[data-cm-modal-next]'),
      current: modal.querySelector('[data-cm-modal-current]'),
      total: modal.querySelector('[data-cm-modal-total]'),
      dots: modal.querySelector('[data-cm-modal-dots]'),
      eyebrow: modal.querySelector('[data-cm-modal-eyebrow]'),
      title: modal.querySelector('[data-cm-modal-title]'),
      price: modal.querySelector('[data-cm-modal-price]'),
      description: modal.querySelector('[data-cm-modal-description]'),
      link: modal.querySelector('[data-cm-modal-link]'),
    };

    let slideCount = 0;

    function buildSlides(imagesTpl) {
      m.slides.innerHTML = '';
      m.dots.innerHTML = '';

      const imgs = imagesTpl
        ? Array.from(imagesTpl.content.querySelectorAll('img'))
        : [];

      slideCount = imgs.length;

      if (slideCount === 0) {
        // Placeholder slide
        const li = document.createElement('li');
        li.className = 'cm-modal__slide';
        m.slides.appendChild(li);
      } else {
        imgs.forEach((img, i) => {
          const li = document.createElement('li');
          li.className = 'cm-modal__slide';
          const clone = img.cloneNode(true);
          // First slide loads eagerly so it's instant
          if (i === 0) clone.loading = 'eager';
          li.appendChild(clone);
          m.slides.appendChild(li);

          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'cm-modal__dot';
          dot.setAttribute('role', 'tab');
          dot.setAttribute('aria-label', `Go to image ${i + 1}`);
          dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
          dot.dataset.index = String(i);
          m.dots.appendChild(dot);
        });
      }

      m.gallery.toggleAttribute('data-single', slideCount <= 1);
      m.total.textContent = String(Math.max(slideCount, 1));
      setActiveIndex(0);
      m.slides.scrollLeft = 0;
    }

    function setActiveIndex(idx) {
      m.current.textContent = String(idx + 1);
      m.dots.querySelectorAll('.cm-modal__dot').forEach((d, i) => {
        d.setAttribute('aria-selected', i === idx ? 'true' : 'false');
      });
      m.prev.disabled = idx === 0;
      m.next.disabled = idx >= slideCount - 1;
    }

    function navigate(direction) {
      const slideWidth = m.slides.offsetWidth;
      m.slides.scrollBy({ left: slideWidth * direction, behavior: 'smooth' });
    }

    function goTo(idx) {
      const slideWidth = m.slides.offsetWidth;
      m.slides.scrollTo({ left: slideWidth * idx, behavior: 'smooth' });
    }

    // Update active index as user scrolls (debounced via requestAnimationFrame)
    let scrollFrame = 0;
    m.slides.addEventListener('scroll', () => {
      if (scrollFrame) return;
      scrollFrame = requestAnimationFrame(() => {
        const slideWidth = m.slides.offsetWidth;
        const idx = Math.round(m.slides.scrollLeft / slideWidth);
        setActiveIndex(Math.max(0, Math.min(idx, slideCount - 1)));
        scrollFrame = 0;
      });
    });

    m.prev.addEventListener('click', () => navigate(-1));
    m.next.addEventListener('click', () => navigate(1));
    m.dots.addEventListener('click', (e) => {
      const dot = e.target.closest('.cm-modal__dot');
      if (!dot) return;
      goTo(parseInt(dot.dataset.index, 10));
    });

    // Keyboard arrow navigation when modal is open
    modal.addEventListener('keydown', (e) => {
      if (slideCount <= 1) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); navigate(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); navigate(1); }
    });

    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-cm-info-trigger]');
      if (!trigger) return;
      e.preventDefault();

      const item = trigger.closest('.cm-addons-card__item');
      const imagesTpl = item ? item.querySelector('[data-cm-info-images]') : null;
      const specsTpl = item ? item.querySelector('[data-cm-info-content]') : null;

      buildSlides(imagesTpl);

      m.eyebrow.textContent = trigger.dataset.infoEyebrow || '';
      m.eyebrow.toggleAttribute('hidden', !trigger.dataset.infoEyebrow);
      m.title.textContent = trigger.dataset.infoTitle || '';
      m.price.textContent = trigger.dataset.infoPrice || '';
      // Hide the "View full product page" link for hidden addons
      // (licenses, future internal-only SKUs). When data-info-link is
      // empty the snippet has opted out of letting the customer reach
      // the standalone PDP — keep them on the router page.
      var addonLink = trigger.dataset.infoLink || '';
      if (addonLink) {
        m.link.href = addonLink;
        m.link.hidden = false;
      } else {
        m.link.href = '#';
        m.link.hidden = true;
      }
      m.description.innerHTML = specsTpl ? specsTpl.innerHTML : '';

      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
    });

    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
      if (e.target.closest('[data-cm-modal-close]')) modal.close();
    });
  }

  // Hook the main product form's Add-to-Cart click. Shopify product forms
  // post to /cart/add — we intercept, fire the original add, then add each
  // selected addon. After all are added, dispatch a cart:refresh and open
  // the cart drawer (Impulse listens for it).
  function attach() {
    const productForm = document.querySelector('form[action*="/cart/add"]');
    if (!productForm) return false;

    productForm.addEventListener('submit', async (e) => {
      const selected = getSelectedAddons();
      if (selected.length === 0) return; // No addons → let Impulse handle normally

      e.preventDefault();
      e.stopImmediatePropagation();

      const formData = new FormData(productForm);
      const mainItem = {
        id: parseInt(formData.get('id'), 10),
        quantity: parseInt(formData.get('quantity') || '1', 10),
        properties: {},
      };
      // Tag main item so the order ops team sees it was a kit purchase
      mainItem.properties['_kit_with'] = selected
        .map((el) => el.dataset.productHandle)
        .join(', ');

      const items = [
        mainItem,
        ...selected.map((el) => ({
          id: parseInt(el.dataset.variantId, 10),
          quantity: 1,
          properties: { _kit_for: formData.get('id') },
        })),
      ];

      try {
        const submitButton = productForm.querySelector('[type="submit"]');
        if (submitButton) submitButton.classList.add('btn--loading');

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items }),
        });

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error('[cm-addons] add failed', err);
          // Fallback: native submit so user still sees Shopify's error UI
          productForm.submit();
          return;
        }

        // Trigger Impulse's cart drawer / refresh
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));

        // If the theme doesn't pick up the events, navigate to /cart as fallback
        setTimeout(() => {
          if (!document.querySelector('.cart-drawer.is-active, .cart-drawer--open')) {
            window.location.href = '/cart';
          }
        }, 600);
      } catch (err) {
        console.error('[cm-addons] error', err);
        productForm.submit();
      } finally {
        const submitButton = productForm.querySelector('[type="submit"]');
        if (submitButton) submitButton.classList.remove('btn--loading');
      }
    }, true); // capture phase, runs before Impulse's own handler

    return true;
  }

  // Try to attach immediately; if the form isn't ready, retry a few times
  if (!attach()) {
    let tries = 0;
    const interval = setInterval(() => {
      if (attach() || ++tries > 20) clearInterval(interval);
    }, 150);
  }
})();
